import React from "react";
import { Info, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type {
  CloudProviderConfig,
  CloudProviderField,
} from "@/types/onboarding";

interface FormFieldProps {
  field: CloudProviderField;
  value: string;
  onChange: (fieldName: string, value: string) => void;
}

const FormField: React.FC<FormFieldProps> = ({ field, value, onChange }) => {
  const isSecretField =
    field.name.toLowerCase().includes("secret") ||
    field.name.toLowerCase().includes("key") ||
    field.name.toLowerCase().includes("token");

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label
          htmlFor={field.name}
          className="text-base font-medium text-gray-900"
        >
          {field.label}
        </Label>
        {field.hasInfo && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Your GCP access token with required permissions</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {field.type === "textarea" ? (
        <Textarea
          id={field.name}
          placeholder={field.placeholder}
          value={value}
          onChange={(e) => onChange(field.name, e.target.value)}
          className="h-32 resize-none focus:ring-[#3CC858]"
        />
      ) : (
        <Input
          id={field.name}
          type={isSecretField ? "password" : "text"}
          placeholder={field.placeholder}
          value={value}
          onChange={(e) => onChange(field.name, e.target.value)}
          className="focus:ring-[#3CC858]"
        />
      )}
    </div>
  );
};

export interface ProviderSetupFormProps {
  config: CloudProviderConfig;
  formData: Record<string, string>;
  isLoading: boolean;
  isSaving: boolean;
  testResult: {
    success: boolean;
    message: string;
    isDetailedError?: boolean;
  } | null;
  saveErrorMessage?: string | null;
  onInputChange: (fieldName: string, value: string) => void;
  onTestConnection: () => void;
  onSaveAndContinue: () => void;
}

const ProviderSetupForm: React.FC<ProviderSetupFormProps> = ({
  config,
  formData,
  isLoading,
  isSaving,
  testResult,
  saveErrorMessage,
  onInputChange,
  onTestConnection,
  onSaveAndContinue,
}) => {
  return (
    <div className="w-full max-w-2xl mx-auto bg-white p-6 px-10">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 flex items-center justify-center">
            <img
              src={config.logo}
              alt={config.name}
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {config.shortName}
          </h1>
        </div>
      </div>

      {testResult && (
        <Alert
          className={`mb-6 ${
            testResult.success ? "border-green-500" : "border-red-500"
          }`}
        >
          <div className="flex items-start gap-3">
            {testResult.success ? (
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
            ) : (
              <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            )}
            <AlertDescription
              className={`${
                testResult.success ? "text-green-700" : "text-red-700"
              } ${testResult.isDetailedError ? "leading-relaxed" : ""}`}
            >
              {testResult.message}
            </AlertDescription>
          </div>
        </Alert>
      )}

      {testResult?.success && saveErrorMessage && (
        <Alert className="mb-6 border-red-500">
          <div className="flex items-start gap-3">
            <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <AlertDescription className="text-red-700 leading-relaxed">
              {saveErrorMessage}
            </AlertDescription>
          </div>
        </Alert>
      )}

      <div className="space-y-6">
        {config.fields.map((field) => (
          <FormField
            key={field.name}
            field={field}
            value={formData[field.name] || ""}
            onChange={onInputChange}
          />
        ))}
      </div>

      <div className="mt-8 flex gap-4">
        <Button
          onClick={onTestConnection}
          variant="outline"
          className="px-6 py-2"
          disabled={
            isLoading ||
            isSaving ||
            config.fields
              .filter((f) => !f.label.toLowerCase().includes("(optional)"))
              .some((f) => !formData[f.name])
          }
        >
          {isLoading ? "Testing..." : "Test Connection"}
        </Button>

        {testResult?.success && (
          <Button
            onClick={onSaveAndContinue}
            className="px-6 py-2 bg-[#3CC858] hover:bg-[#34B04A] flex items-center justify-center"
            disabled={isLoading || isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              "Save & Continue"
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProviderSetupForm;
